import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { withoutBidiControls as plain } from "../snippets/bidi";
import { companyPath, thesisPath } from "./lib/format";
import { formatPct } from "./lib/money";

/**
 * A target version's holdings (above 0%) with their weights, sectors,
 * primary theses and reasons, then cash — what the account should hold.
 * Names, sectors and reasons are agent-written: plain text without bidi
 * controls (D8), wrapping anywhere on a phone.
 */
function TargetHoldingsList({ version, label }) {
  // A 0% holding is a sale in this version, not something it holds.
  const held = (Array.isArray(version.holdings) ? version.holdings : []).filter(
    (holding) => Number(holding.weight) > 0,
  );
  return (
    <ul aria-label={label} className="m-0 flex list-none flex-col p-0">
      {held.map((holding) => (
        <li key={holding.symbol} data-holding={holding.symbol} className="border-b border-border py-2">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <Link
              to={companyPath(holding.symbol)}
              className="inline-flex min-h-11 min-w-11 items-center font-semibold underline-offset-2 hover:underline md:min-h-0 md:min-w-0"
            >
              {holding.symbol}
            </Link>{" "}
            <span className="min-w-0 flex-1 text-muted-foreground wrap-anywhere">{plain(holding.name)}</span>{" "}
            <span className="font-semibold tabular-nums">{formatPct(holding.weight)}</span>
          </div>
          <p className="m-0 text-sm text-muted-foreground wrap-anywhere">
            {holding.sector ? `${plain(holding.sector)} · ` : ""}
            {holding.primary_thesis ? (
              <>
                Primary thesis:{" "}
                <Link
                  to={thesisPath(holding.primary_thesis)}
                  className="inline-flex min-h-11 items-center underline underline-offset-2 hover:text-foreground md:min-h-0"
                >
                  {holding.primary_thesis}
                </Link>
              </>
            ) : (
              "No primary thesis"
            )}
          </p>
          {holding.reason && <p className="m-0 text-sm wrap-anywhere">{plain(holding.reason)}</p>}
        </li>
      ))}
      <li className="flex items-baseline gap-x-2 py-2">
        <span className="flex-1 font-semibold">Cash</span>{" "}
        <span className="font-semibold tabular-nums">{formatPct(version.cash_weight)}</span>
      </li>
    </ul>
  );
}

TargetHoldingsList.propTypes = {
  version: PropTypes.shape({
    cash_weight: PropTypes.string.isRequired,
    holdings: PropTypes.arrayOf(
      PropTypes.shape({
        symbol: PropTypes.string.isRequired,
        name: PropTypes.string,
        sector: PropTypes.string,
        weight: PropTypes.string.isRequired,
        reason: PropTypes.string,
        primary_thesis: PropTypes.string,
      }),
    ),
  }).isRequired,
  /** The list's accessible name. */
  label: PropTypes.string.isRequired,
};

export default TargetHoldingsList;
